import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Portal,
  TextField,
} from '@mui/material'
import {
  ItemsDocument,
  SelectItem,
  useLocationOptionsByFormQuery,
  useUpdateItemsLocationMutation,
} from '@kistill/client/data-access'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useState, useEffect } from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import { KistillFormContainer } from '../Form/KistillFormContainer'
import { updateDelay } from '../../utils/updateDelay'
import Draggable from 'react-draggable'

interface UpdateItemsLocationProps {
  setOpen: (value: boolean) => void
  open: boolean
  handleClose: () => void
  itemGuids: string[]
  type?: string | undefined
  updateListViewData?: () => void
  /** Refetch the list query after updating locations so the table shows new data. */
  refetchList?: () => void | Promise<void>
}

export function UpdateItemsLocation({
  setOpen,
  open,
  itemGuids,
  type,
  updateListViewData,
  refetchList,
}: UpdateItemsLocationProps) {
  const [locationGuidsToRemove, setLocationGuidsToRemove] = useState<string[]>(
    [],
  )
  const [locationGuidsToAdd, setLocationGuidsToAdd] = useState<string[]>([])
  const [canRemoveLastLocation, setCanRemoveLastLocation] =
    useState<boolean>(false)
  const [canRemoveLastLtoLocation, setCanRemoveLastLtoLocation] =
    useState<boolean>(false)

  const [coldLocationGuidsToRemove, setColdLocationGuidsToRemove] = useState<
    string[]
  >([])
  const [coldLocationGuidsToAdd, setColdLocationGuidsToAdd] = useState<
    string[]
  >([])
  const [isClosing, setIsClosing] = useState(false)

  const { control } = useForm()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    if (!open) setIsClosing(false)
  }, [open])

  const { data: locationsData } = useLocationOptionsByFormQuery({
    variables: {
      formCode: type,
    },
  })
  const locationsOptions = locationsData?.locationOptionsByForm as SelectItem[]

  const coldLocationsOptions =
    locationsData?.coldLocationOptionsByForm as SelectItem[]

  function setToFalse() {
    setCanRemoveLastLocation(false)
    if (setOpen) {
      setOpen(false)
    }
  }

  const [updateItemsLocationMutation, { loading }] =
    useUpdateItemsLocationMutation({
      onCompleted: async () => {
        setIsClosing(true)
        if (!refetchList && updateListViewData) {
          updateListViewData()
        }
        enqueueSnackbar('Staðsetning var uppfærð á völdum eintökum', {
          variant: 'success',
        })
        if (refetchList) {
          await updateDelay({
            delayMs: 1000,
            times: 3,
            then: async () => {
              await refetchList()
            },
          })
        }
        setCanRemoveLastLocation(false)
        setOpen(false)
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        enqueueSnackbar(`${error.message}`, {
          variant: 'error',
        })
      },
    })

  const updateItemsLocation = async () => {
    await updateItemsLocationMutation({
      variables: {
        itemGuids: itemGuids,
        updateItemsLocationInput: {
          locationsGuidToRemove: locationGuidsToRemove,
          locationsGuidToAdd: locationGuidsToAdd,
          canRemoveLastLocation: canRemoveLastLocation,
        },
        updateColdLocationInput: {
          coldLocationsGuidToRemove: coldLocationGuidsToRemove,
          coldLocationsGuidToAdd: coldLocationGuidsToAdd,
          canRemoveLastColdLocation: canRemoveLastLtoLocation,
        },
      },
      refetchQueries: [
        {
          query: ItemsDocument,
        },
      ],
    })
  }

  // Handle locations

  const handleInputRemoveGuidLocationGuid = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    data: SelectItem[],
  ) => {
    setLocationGuidsToRemove(data.map((item) => item.id))
  }

  const handleInputAddGuidLocationGuid = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    data: SelectItem[],
  ) => {
    setLocationGuidsToAdd(data.map((item) => item.id))
  }

  // Handle cold locations

  const handleInputRemoveGuidColdLocationGuid = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    data: SelectItem[],
  ) => {
    setColdLocationGuidsToRemove(data.map((item) => item.id))
  }

  const handleInputAddGuidColdLocationGuid = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    data: SelectItem[],
  ) => {
    setColdLocationGuidsToAdd(data.map((item) => item.id))
  }

  const handleLocationCheckboxChange = () => {
    setCanRemoveLastLocation(!canRemoveLastLocation)
  }

  const handleLtoLocationCheckboxChange = () => {
    setCanRemoveLastLtoLocation(!canRemoveLastLtoLocation)
  }

  return (
    <Box style={{ position: 'relative' }}>
      <Portal>
        <Backdrop open={open} style={{ zIndex: 1300, position: 'fixed' }} />
      </Portal>
      <Draggable>
        <Dialog
          fullWidth
          open={open}
          onClose={() => setToFalse()}
          hideBackdrop={true}
          PaperProps={{
            sx: { maxWidth: 'none', width: '70%' },
          }}
        >
          <KistillFormContainer onSuccess={updateItemsLocation}>
            <DialogTitle sx={{ pt: 5 }}>
              Breyta staðsetningum á völdum eintökum
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} p={2}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name='locationGuidsToRemove'
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        multiple
                        options={locationsOptions.filter(
                          (option: SelectItem) =>
                            !locationGuidsToRemove.find(
                              (guid) => guid === option.id,
                            ),
                        )}
                        getOptionLabel={(option: SelectItem) => option.title}
                        onChange={(event, data) => {
                          handleInputRemoveGuidLocationGuid(event, data)
                          onChange(data)
                        }}
                        value={value || []}
                        renderOption={(props, option: SelectItem) => (
                          <li {...props} key={option.id}>
                            {`${option.title} `}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Staðsetningar til að fjarlægja af völdum eintökum'
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name='locationGuidsToAdd'
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        multiple
                        options={locationsOptions.filter(
                          (option: SelectItem) =>
                            !locationGuidsToAdd.find(
                              (guid) => guid === option.id,
                            ),
                        )}
                        getOptionLabel={(option: SelectItem) => option.title}
                        onChange={(event, data) => {
                          handleInputAddGuidLocationGuid(event, data)
                          onChange(data)
                        }}
                        value={value || []}
                        renderOption={(props, option: SelectItem) => (
                          <li {...props} key={option.id}>
                            {`${option.title} `}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Staðsetningar til að bæta við valin eintök'
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                name='kreditFullskradur'
                control={<Checkbox onChange={handleLocationCheckboxChange} />}
                label='Það má fjarlægja staðsetningu þó hún sé eina staðsetning eintaks.'
              />
            </DialogContent>
            <DialogTitle sx={{ pt: 5 }}>
              Breyta LTO staðsetningu á völdum eintökum
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} p={2}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name='coldLocationGuidsToRemove'
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        multiple
                        options={coldLocationsOptions.filter(
                          (option: SelectItem) =>
                            !coldLocationGuidsToRemove.find(
                              (guid) => guid === option.id,
                            ),
                        )}
                        getOptionLabel={(option: SelectItem) => option.title}
                        onChange={(event, data) => {
                          handleInputRemoveGuidColdLocationGuid(event, data)
                          onChange(data)
                        }}
                        value={value || []}
                        renderOption={(props, option: SelectItem) => (
                          <li {...props} key={option.id}>
                            {`${option.title} `}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='LTO Staðsetningar til að fjarlægja af völdum eintökum'
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name='coldLocationGuidsToAdd'
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        multiple
                        options={coldLocationsOptions.filter(
                          (option: SelectItem) =>
                            !coldLocationGuidsToAdd.find(
                              (guid) => guid === option.id,
                            ),
                        )}
                        getOptionLabel={(option: SelectItem) => option.title}
                        onChange={(event, data) => {
                          handleInputAddGuidColdLocationGuid(event, data)
                          onChange(data)
                        }}
                        value={value || []}
                        renderOption={(props, option: SelectItem) => (
                          <li {...props} key={option.id}>
                            {`${option.title} `}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='LTO Staðsetningar til að bæta við valin eintök'
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                name='kreditFullskradur'
                control={
                  <Checkbox onChange={handleLtoLocationCheckboxChange} />
                }
                label='Það má fjarlægja LTO staðsetningu þó hún sé eina staðsetning eintaks.'
              />
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={() => setToFalse()}>
                Til Baka
              </Button>
              <LoadingButton
                variant='contained'
                loading={loading || isClosing}
                type={'submit'}
                onClick={() => updateItemsLocation()}
                sx={{ ml: 2 }}
              >
                Breyta staðsetningu
              </LoadingButton>
            </DialogActions>
          </KistillFormContainer>
        </Dialog>
      </Draggable>
    </Box>
  )
}
